import { DateTime } from 'luxon';

import { type LogDetails } from '../../api/index.js';

export type DstChange = 'fall-back' | 'spring-forward';

export interface HourlyLogs {
  label: string;
  logs: LogDetails[];
  dstChange?: DstChange;
}

const getDateTime = (time: string, timezone: string) => DateTime.fromISO(time).setZone(timezone);

const getHourKey = (dateTime: DateTime) => dateTime.startOf('hour').toMillis();

function getLogDuration(
  currentLogDateTime: DateTime,
  nextLogDateTime: DateTime,
  startDateTime: DateTime,
  endDateTime: DateTime,
  position: { firstLog: boolean; lastLog: boolean },
): number {
  if (position.lastLog && currentLogDateTime < endDateTime) {
    return endDateTime.diff(currentLogDateTime, 'seconds').seconds;
  }
  if (position.firstLog) {
    return nextLogDateTime.diff(startDateTime, 'seconds').seconds;
  }
  if (position.lastLog) {
    return 0;
  }
  if (getHourKey(nextLogDateTime) !== getHourKey(currentLogDateTime)) {
    return nextLogDateTime.startOf('hour').diff(currentLogDateTime, 'seconds').seconds;
  }
  return nextLogDateTime.diff(currentLogDateTime, 'seconds').seconds;
}

function getBucketHourKey(logDateTime: DateTime, startDateTime: DateTime, endDateTime: DateTime): number {
  if (logDateTime < startDateTime) {
    return getHourKey(startDateTime);
  }
  if (logDateTime > endDateTime) {
    return getHourKey(endDateTime.minus({ hour: 1 }));
  }
  return getHourKey(logDateTime);
}

function findDstChangeHour(startDateTime: DateTime, endDateTime: DateTime): DateTime | null {
  for (let hour = startDateTime.startOf('hour').plus({ hours: 1 }); hour < endDateTime; hour = hour.plus({ hours: 1 })) {
    if (hour.offset !== hour.minus({ hours: 1 }).offset) {
      return hour;
    }
  }
  return null;
}

function skippedHourLabel(dstChangeHour: DateTime): string {
  return DateTime.fromObject({ hour: (dstChangeHour.hour + 23) % 24 }, { zone: 'utc' }).toFormat('h a');
}

function markDstChange(rows: HourlyLogs[], hourKeys: number[], startDateTime: DateTime, endDateTime: DateTime): void {
  const dstChangeHour = findDstChangeHour(startDateTime, endDateTime);
  if (!dstChangeHour) {
    return;
  }

  const dstChangeKey = dstChangeHour.toMillis();
  const dstChangeIndex = hourKeys.findIndex((hourKey) => hourKey >= dstChangeKey);
  const isFallBack = dstChangeHour.offset < dstChangeHour.minus({ hours: 1 }).offset;

  if (isFallBack) {
    if (hourKeys[dstChangeIndex] === dstChangeKey) {
      rows[dstChangeIndex].dstChange = 'fall-back';
    }
    return;
  }

  const insertIndex = dstChangeIndex === -1 ? rows.length : dstChangeIndex;
  rows.splice(insertIndex, 0, { label: skippedHourLabel(dstChangeHour), logs: [], dstChange: 'spring-forward' });
}

export function groupLogsByHour(
  logs: LogDetails[],
  startTime: string,
  endTime: string,
  timezone: string,
): HourlyLogs[] {
  const logsPerHour = new Map<number, LogDetails[]>();
  const startDateTime = getDateTime(startTime, timezone);
  const endDateTime = getDateTime(endTime, timezone);

  for (let i = 0; i < logs.length; i++) {
    const currentLog = logs[i];
    const firstLog = i === 0;
    const lastLog = i === logs.length - 1;
    const nextLog = lastLog ? { time: endTime } : logs[i + 1];
    const currentLogDateTime = getDateTime(currentLog.time, timezone);
    const nextLogDateTime = getDateTime(nextLog.time, timezone);

    const duration = getLogDuration(currentLogDateTime, nextLogDateTime, startDateTime, endDateTime, {
      firstLog,
      lastLog,
    });

    const crossesIntoNextHour = !firstLog && !lastLog
      && getHourKey(nextLogDateTime) !== getHourKey(currentLogDateTime);
    if (crossesIntoNextHour && startDateTime < nextLogDateTime && endDateTime > nextLogDateTime) {
      const nextDuration = nextLogDateTime.diff(currentLogDateTime.plus({ hour: 1 }).startOf('hour'), 'seconds').seconds;
      logsPerHour.set(getHourKey(nextLogDateTime), [{ ...currentLog, duration: nextDuration }]);
    }

    const hourKey = getBucketHourKey(currentLogDateTime, startDateTime, endDateTime);
    const hourLogs = logsPerHour.get(hourKey) ?? [];
    hourLogs.push({ ...currentLog, duration });
    logsPerHour.set(hourKey, hourLogs);
  }

  const hourKeys = [...logsPerHour.keys()].sort((a, b) => a - b);
  const rows: HourlyLogs[] = hourKeys.map((hourKey) => ({
    label: DateTime.fromMillis(hourKey, { zone: timezone }).toFormat('h a'),
    logs: logsPerHour.get(hourKey) ?? [],
  }));

  markDstChange(rows, hourKeys, startDateTime, endDateTime);

  return rows;
}
